import { Spine, type SkeletonData } from '@esotericsoftware/spine-pixi-v8';
import { Rectangle, type Renderer } from 'pixi.js';
import type { MagicLayout } from '@magic/layout';
import { getGameConfig } from 'config/game.config';

/** Id (or id prefix) of the game's symbol export — the same one the reels take their pool from. */
const SYMBOL_SPINE = 'symbol';
/**
 * Pixels per skeleton unit in the rendered images. The symbol art is authored 1:1 with the
 * skeleton, and the pages that show it draw it a few dozen pixels tall, so 1 already has detail
 * to spare — there is nothing above it to render, only bigger data URLs.
 */
const RESOLUTION = 1;
/** Bleed (skeleton units) around the shared box, so antialiasing at the edge of the art is not cut. */
const BLEED = 2;

/**
 * Images of the game's symbols, for the pages that name a symbol outside the reels — the rules
 * page writes `` `H1` `` and wants the art rather than the code.
 *
 * They are rendered from the game's own symbol export when the game starts: one throwaway Spine,
 * built off the scene from the symbol skeleton the reels use, posed in each symbol's skin in turn
 * and captured to a PNG data URL. A symbol on the page is therefore identical to the one on the
 * reels — it *is* the same asset, drawn by the same renderer — with no second set of files to
 * ship and keep in step with the art.
 *
 * Every symbol is drawn into one shared box (the union of all of their bounds), so the images all
 * come out the same size and a single CSS height lines them up however wide or tall each symbol's
 * own art happens to be.
 */
export class SymbolImagesController {
    /** symbol name -> PNG data URL. Filled by the time {@link ready} resolves. */
    private images = new Map<string, string>();
    /** The same images under upper-cased names, so a page may write `h1` for `H1`. */
    private byUpperCase = new Map<string, string>();

    /** Resolves once every symbol has been rendered (or failed to be). */
    readonly ready: Promise<void>;

    constructor(
        private layout: MagicLayout,
        private renderer: Renderer,
    ) {
        this.ready = this.render();
    }

    /** The image for a symbol, by name — case-insensitively, as pages are written by hand. */
    get(name: string): string | undefined {
        return this.images.get(name) ?? this.byUpperCase.get(name.toUpperCase());
    }

    private async render(): Promise<void> {
        const skeletonData = this.symbolSkeleton();
        if (!skeletonData) return;

        const { symbols, specialSymbols } = getGameConfig();
        // the wild and the scatter are as much a part of the rules as the paying symbols, and a
        // game is free to list one in both arrays
        const names = [...new Set([...symbols, ...specialSymbols])];

        // off the scene and off the ticker: nothing drives this one but the poses below, and it
        // is never added to a parent, so it exists only to be rendered into a texture
        const spine = new Spine({ skeletonData, autoUpdate: false });
        // only the names this export actually has a skin for — a config can list a symbol the art
        // does not (yet) cover, and that must not cost the rest their images
        const posed = names.filter((name) => this.pose(spine, name));

        if (posed.length) {
            const frame = this.sharedFrame(spine, posed);

            for (const name of posed) {
                this.pose(spine, name);
                await this.capture(spine, name, frame);
            }
        }

        spine.destroy();
    }

    /** Renders the currently posed skin and records it under `name`. */
    private async capture(spine: Spine, name: string, frame: Rectangle) {
        try {
            // base64 rasterises the pose synchronously and only the encoding is awaited, so the
            // next symbol can be posed as soon as this resolves
            const image = await this.renderer.extract.base64({
                target: spine,
                frame,
                resolution: RESOLUTION,
            });

            this.images.set(name, image);
            this.byUpperCase.set(name.toUpperCase(), image);
        } catch (error) {
            console.error(`Failed to render the "${name}" symbol image:`, error);
        }
    }

    /**
     * The box every symbol is drawn into: the union of their own bounds, plus a little bleed.
     * Shared, so the images come out one size and at their true relative scale — a symbol whose
     * art is shorter than the rest sits smaller in the box instead of being blown up to fill it.
     */
    private sharedFrame(spine: Spine, names: string[]): Rectangle {
        const box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };

        names.forEach((name) => {
            this.pose(spine, name);

            // read straight out: `bounds` hands back the spine's own object, which the next pose
            // overwrites
            const { minX, minY, maxX, maxY } = spine.bounds;

            box.minX = Math.min(box.minX, minX);
            box.minY = Math.min(box.minY, minY);
            box.maxX = Math.max(box.maxX, maxX);
            box.maxY = Math.max(box.maxY, maxY);
        });

        return new Rectangle(
            box.minX - BLEED,
            box.minY - BLEED,
            box.maxX - box.minX + BLEED * 2,
            box.maxY - box.minY + BLEED * 2,
        );
    }

    /**
     * Poses the spine in one symbol's skin, on the setup pose — no animation, so what comes out
     * is the symbol as the reels show it at rest. The state update is what applies the new skin's
     * attachments and re-measures the bounds; without it the spine would still hold the last one.
     */
    private pose(spine: Spine, name: string): boolean {
        const skin = spine.skeleton.data.findSkin(name);

        if (!skin) {
            console.warn(`Symbol images: the "${SYMBOL_SPINE}" spine has no "${name}" skin`);
            return false;
        }

        spine.skeleton.setSkin(skin);
        spine.skeleton.setupPoseSlots();
        spine.update(0);

        return true;
    }

    /** The symbol export's skeleton data, taken off the pool the reels were built from. */
    private symbolSkeleton(): SkeletonData | undefined {
        const [symbol] = this.layout.spine.getSpinesByNamePattern(SYMBOL_SPINE).values();

        if (!symbol) {
            console.warn(`Symbol images: no "${SYMBOL_SPINE}" spine is registered`);
            return;
        }

        return symbol.skeleton.data;
    }
}
